import {
  DownloadButtonProps,
  DownloadButtonStateProps
} from '../../types/components/products/DownloadButton';
import { User } from '../../types/api/UsersTypes';
import { SubscriptionDetail } from '../../types/api/SubscriptionTypes';
import { ApplicationState } from '../../types/state/storeTypes';
import { CollectionsShowResponse } from '../../types/api/CollectionsShowTypes';

import { useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { showUpdateLicenseModal } from '../../state/actions/licenseFlowActions';

import { DownloadIcon } from '../../modules/Icons';
import DownloadList from '../collections/DownloadList';
import CollectionDownloadList from '../collections/CollectionDownloadList';
import { Tooltip } from 'react-tooltip';
import DetectOutsideClick from '../helpers/DetectOutsideClick';
import userHelpers, { userIsConfirmed } from '../../helpers/user';

const DownloadButton: React.FC<DownloadButtonProps> = (props) => {
  const {
    user,
    downloadedItem,
    downloads,
    mode,
    subscription,
    className,
    singleProductCollectionDownload,
    setIsConfirmLicenseFlowOpen,
    collectionId,
    isPfl = false,
    setEmailVerificationModalOpen,
    disableDownloadButton
  } = props;
  const dispatch = useDispatch();
  const { shouldSeeConfirmLicenseFlow } = userHelpers;

  const [downloadDropdownOpen, setDownloadDropdownOpen] = useState(false);
  const localValidation = shouldSeeConfirmLicenseFlow(user);

  const handleDownloadClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    const checkProfile = (!user?.token || !!userIsConfirmed(user)) && !downloadedItem.free_product;
    if (isPfl && !checkProfile) {
      setEmailVerificationModalOpen(true);
      return;
    }

    if (user?.team?.licensee_profile?.invalidated !== false && !isPfl && !singleProductCollectionDownload && localValidation) {
      setIsConfirmLicenseFlowOpen ?
        setIsConfirmLicenseFlowOpen(true) :
        dispatch(showUpdateLicenseModal());
      return;
    }
    setDownloadDropdownOpen(true);
  };

  const wrapInTooltip = (
    content: React.ReactElement,
    tooltipAdditionalStyles?: any
  ): React.ReactElement => {
    return (
      <Tooltip
        clickable={true}
        isOpen={downloadDropdownOpen}
        openOnClick={true}
        id={`downloads-${downloadedItem.id}-${mode}`}
        place="bottom"
        noArrow={true}
        offset={0}
        opacity={1}
        style={{
          boxShadow: '0px 4px 30px 0px rgba(0, 0, 0, 0.25)',
          background: '#0B1113',
          zIndex: 50,
          ...tooltipAdditionalStyles
        }}
        afterShow={async () => {}}
        afterHide={() => {}}>
        {content}
      </Tooltip>
    );
  };

  const renderProductDownloadsList = (): React.ReactElement => {
    if (mode === 'collection-page' && !singleProductCollectionDownload) return;
    const component = (
      <DownloadList
        product={downloadedItem}
        downloads={downloads}
        user={user}
        isOpen={downloadDropdownOpen}
        onDownloadLimitModalClose={() => setDownloadDropdownOpen(false)}
        onDownloadStarted={() => setDownloadDropdownOpen(false)}
        userSubscription={subscription}
        collectionId={collectionId}
      />
    );
    let tooltipAdditionalStyles = {};
    if (singleProductCollectionDownload) tooltipAdditionalStyles = { width: '100%' };
    return wrapInTooltip(component, tooltipAdditionalStyles);
  };

  const renderCollectionDownloadsList = (): React.ReactElement => {
    if (mode !== 'collection-page' || !!singleProductCollectionDownload) return;
    const component = (
      <CollectionDownloadList
        collection={downloadedItem as CollectionsShowResponse}
        downloads={downloads}
      />
    );
    return wrapInTooltip(component, { width: '100%' });
  };

  const renderProductGridItemDownload = (): React.ReactElement => {
    return (
      <button
        cy-test-id="product-grid-item-button"
        className="clip-button hover:stroke-a-blue"
        data-tooltip-id={`downloads-${downloadedItem.id}-product-grid-item`}>
        <DownloadIcon onClick={handleDownloadClick} />
      </button>
    );
  };

  const renderProductModalDownload = (): React.ReactElement => {
    return (
      <button
        cy-test-id="product-modal-button"
        data-tooltip-id={`downloads-${downloadedItem.id}-product-modal`}
        className="my-4 rounded-[5px] px-5 flex align-middle items-center gap-2 justify-center bg-a-blue color-white border-0 w-full uppercase text-[16px] text-center font-[Eurostile] pt-2 pb-3 font-semibold"
        onClick={handleDownloadClick}>
        <DownloadIcon className="stroke-white" /> Download
      </button>
    );
  };

  const renderCollectionDownload = (): React.ReactElement => {
    return (
      <>
        <button
          cy-test-id="collection-page-button"
          data-tooltip-id={`downloads-${downloadedItem.id}-collection-page`}
          data-tooltip-content={disableDownloadButton ? "Please update your license profile to enable downloads" : ""}
          className={`
          rounded-[5px] px-6 py-4 align-middle mt-[24px]
          uppercase text-[17px] leading-24 eurostile
          w-full flex justify-center gap-2 font-bold
          ${disableDownloadButton
              ? 'bg-a-blue opacity-30'
              : 'bg-a-blue hover:bg-a-blue-light'
            }
        `}
          onClick={handleDownloadClick}
          disabled={disableDownloadButton}
        >
          <DownloadIcon className={`stroke-white ${disableDownloadButton ? 'opacity-50' : ''}`} />
          Download
        </button>

        <Tooltip id={`downloads-${downloadedItem.id}-collection-page`} className='max-w-[300px] z-50 !text-[14px] !normal-case leading-[22px] rounded-[5px] py-[10px] shadow tooltip' opacity={1} />
      </>
    );
  };

  const renderDownloadControl = (): React.ReactElement => {
    switch (mode) {
      case 'product-grid-item':
        return renderProductGridItemDownload();
      case 'product-modal':
        return renderProductModalDownload();
      case 'collection-page':
        return renderCollectionDownload();
      default:
        return <></>;
    }
  };

  return (
    <DetectOutsideClick onOutsideClick={() => setDownloadDropdownOpen(false)}>
      <div className={`relative ${className || ''}`} cy-test-id="download-button">
        {renderDownloadControl()}
        {renderProductDownloadsList()}
        {renderCollectionDownloadsList()}
      </div>
    </DetectOutsideClick>
  );
};

const mapStateToProps = (state: ApplicationState): DownloadButtonStateProps => ({
  user: state.auth.user as User,
  subscription: state.auth.subscription as SubscriptionDetail
});

export default connect(mapStateToProps)(DownloadButton);
